import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const pollingRef = useRef(false);
  const externalRef = searchParams.get('ref') || searchParams.get('external_reference') || searchParams.get('session_id');

  useEffect(() => {
    if (!externalRef) {
      setStatus('success');
      return;
    }

    const pollPaymentStatus = async (attempts = 0) => {
      const maxAttempts = 10;
      const pollInterval = 3000;

      if (attempts >= maxAttempts) {
        setStatus('success');
        return;
      }

      if (pollingRef.current) return;
      pollingRef.current = true;

      try {
        const response = await fetch(
          `${BACKEND_URL}/api/mercadopago/checkout/status/${externalRef}`
        );

        if (!response.ok) {
          setStatus('success');
          pollingRef.current = false;
          return;
        }

        const data = await response.json();
        setPaymentData(data);

        if (data.user_created) {
          setStatus('success');
          pollingRef.current = false;
          return;
        }

        setStatus('processing');
        pollingRef.current = false;
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      } catch (error) {
        console.error('Erro ao verificar status:', error);
        pollingRef.current = false;
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      }
    };

    pollPaymentStatus();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted flex items-center justify-center p-4">
      <Card className="w-full max-w-lg shadow-lg">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4">
            {status === 'success' && (
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            )}
            {(status === 'checking' || status === 'processing') && (
              <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center mx-auto">
                <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
              </div>
            )}
            {(status === 'error' || status === 'expired' || status === 'timeout') && (
              <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto">
                <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            )}
          </div>
          <CardTitle className="text-2xl">
            {status === 'success' && 'Pagamento Confirmado!'}
            {status === 'checking' && 'Verificando Pagamento...'}
            {status === 'processing' && 'Processando Pagamento...'}
            {status === 'error' && 'Erro no Pagamento'}
            {status === 'expired' && 'Sessão Expirada'}
            {status === 'timeout' && 'Tempo Esgotado'}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          {status === 'success' && (
            <>
              <p className="text-muted-foreground">
                Sua assinatura foi ativada com sucesso! Você receberá um email com suas credenciais de acesso.
              </p>
              {paymentData?.metadata?.plan_name && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="font-semibold text-green-800">
                    {paymentData.metadata.plan_name}
                  </p>
                  <p className="text-sm text-green-700 mt-1">
                    Plano ativado para: {paymentData.metadata.email}
                  </p>
                </div>
              )}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
                <p className="text-sm text-amber-800">
                  <strong>Importante:</strong> Uma senha temporária foi gerada para você. Faça login e altere sua senha no primeiro acesso.
                </p>
              </div>
              <Button className="w-full mt-4" onClick={() => navigate('/login')}>
                Ir para Login
              </Button>
            </>
          )}
          {(status === 'checking' || status === 'processing') && (
            <p className="text-muted-foreground">
              Aguarde enquanto verificamos seu pagamento...
            </p>
          )}
          {status === 'error' && (
            <>
              <p className="text-muted-foreground">
                Não foi possível verificar seu pagamento. Se o valor foi cobrado, entre em contato com o suporte.
              </p>
              <Button variant="outline" className="w-full" onClick={() => navigate('/login')}>
                Voltar ao Login
              </Button>
            </>
          )}
          {status === 'expired' && (
            <>
              <p className="text-muted-foreground">
                A sessão de pagamento expirou. Por favor, tente novamente.
              </p>
              <Button variant="outline" className="w-full" onClick={() => navigate('/login')}>
                Voltar ao Login
              </Button>
            </>
          )}
          {status === 'timeout' && (
            <>
              <p className="text-muted-foreground">
                Não conseguimos confirmar seu pagamento a tempo. Verifique seu email ou entre em contato com o suporte.
              </p>
              <Button variant="outline" className="w-full" onClick={() => navigate('/login')}>
                Voltar ao Login
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
